import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, ClipboardCheck, AlertTriangle, FileText,
  TrendingUp, Shield, Users, BookOpen, Settings, Bell,
  Search, ChevronDown, Menu, X, Activity, Building2,
  MessageSquare, Calendar, BarChart3, Microscope, Heart,
  LogOut, User, Target
} from 'lucide-react';
import { toast } from 'sonner';
import NotificationBell from '../shared/NotificationBell';

const getNavigation = (userRole) => {
  const allNav = [
    { name: 'Dashboard', href: '/hospital', icon: LayoutDashboard, roles: ['VEO', 'ADMIN', 'DEPT_HEAD', 'STAFF'] },
    { name: 'Safety Events', href: '/hospital/safety-events', icon: AlertTriangle, roles: ['VEO', 'ADMIN', 'DEPT_HEAD', 'STAFF'] },
    { name: 'Report Event', href: '/hospital/report-event', icon: FileText, roles: ['VEO', 'ADMIN', 'DEPT_HEAD', 'STAFF'] },
    { name: 'Safety Intelligence', href: '/hospital/safety-intelligence', icon: Activity, roles: ['VEO', 'ADMIN', 'DEPT_HEAD'] },
    { name: 'Action Plans', href: '/hospital/action-plans', icon: Target, roles: ['VEO', 'ADMIN', 'DEPT_HEAD'] },
    { name: 'Audits & Rounds', href: '/hospital/audits', icon: ClipboardCheck, roles: ['VEO', 'ADMIN', 'DEPT_HEAD'] },
    { name: 'Risk Register', href: '/hospital/risk-register', icon: Shield, roles: ['VEO', 'ADMIN', 'DEPT_HEAD'] },
    { name: 'PI Projects', href: '/hospital/pi-projects', icon: TrendingUp, roles: ['VEO', 'ADMIN', 'DEPT_HEAD'] },
    { name: 'Infection Control', href: '/hospital/infection-control', icon: Microscope, roles: ['VEO', 'ADMIN', 'DEPT_HEAD'] },
    { name: 'Policies', href: '/hospital/policies', icon: FileText, roles: ['VEO', 'ADMIN', 'DEPT_HEAD', 'STAFF'] },
    { name: 'Training', href: '/hospital/training', icon: BookOpen, roles: ['VEO', 'ADMIN', 'DEPT_HEAD', 'STAFF'] },
    { name: 'Ask Niko', href: '/hospital/ask-niko', icon: MessageSquare, roles: ['VEO', 'ADMIN', 'DEPT_HEAD', 'STAFF'] },
    { name: 'Settings', href: '/hospital/settings', icon: Settings, roles: ['VEO', 'ADMIN'] },
  ];
  
  return allNav.filter(item => item.roles.includes(userRole));
};

const HospitalLayout = ({ children, userRole = 'DEPT_HEAD' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [user, setUser] = useState(null);
  const navigation = getNavigation(userRole);

  useEffect(() => {
    const storedUser = localStorage.getItem('nikosafe_user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    } else {
      navigate('/');
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('nikosafe_token');
    localStorage.removeItem('nikosafe_user');
    toast.success('Logged out successfully');
    navigate('/');
  };

  const getUserInitial = () => {
    if (user?.name) {
      return user.name.charAt(0).toUpperCase();
    }
    return 'U';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[var(--niko-teal)] to-[var(--niko-green)] flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <span className="font-bold text-gray-900">Metro Health</span>
            <span className="text-[var(--niko-teal)] text-xs block">NikoSafe Connect</span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="ml-auto lg:hidden text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* User Info */}
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="relative">
            <button
              onClick={() => setProfileOpen(!profileOpen)}
              className="flex items-center gap-3 w-full hover:bg-gray-50 rounded-lg p-1 transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--niko-teal)] to-[var(--niko-green)] flex items-center justify-center text-white font-semibold">
                {getUserInitial()}
              </div>
              <div className="flex-1 text-left">
                <p className="font-medium text-gray-900 text-sm">{user?.name || 'User'}</p>
                <p className="text-xs text-gray-500">{user?.organization || 'Healthcare Staff'}</p>
              </div>
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${profileOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {/* Profile Dropdown */}
            {profileOpen && (
              <>
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setProfileOpen(false)}
                />
                <div className="absolute left-0 right-0 top-full mt-2 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
                  <div className="p-2">
                    <Link
                      to="/profile"
                      onClick={() => setProfileOpen(false)}
                      className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      <User className="w-4 h-4" />
                      My Profile
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-3 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors w-full"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="px-3 py-4 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 180px)' }}>
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm ${
                  isActive
                    ? 'bg-[var(--niko-teal)]/10 text-[var(--niko-teal)] font-medium'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <item.icon className={`w-5 h-5 ${isActive ? 'text-[var(--niko-teal)]' : 'text-gray-400'}`} />
                {item.name}
              </Link>
            );
          })}
        </nav>

        {/* Quick Actions */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-100 bg-white">
          <Link to="/hospital/report-event" className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[var(--niko-teal)] to-[var(--niko-green)] text-white rounded-lg font-medium text-sm hover:opacity-90 transition-opacity">
            <AlertTriangle className="w-4 h-4" />
            Report Event
          </Link>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between px-4 lg:px-6 py-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <Menu className="w-5 h-5" />
            </button>

            {/* Search */}
            <div className="hidden md:flex items-center flex-1 max-w-md">
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search events, audits, policies..."
                  className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--niko-teal)] focus:border-transparent"
                />
              </div>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-3">
              <NotificationBell variant="light" />
              <button className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg">
                <Calendar className="w-5 h-5" />
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default HospitalLayout;
